// src/pages/supply-chain/LocationsPage.jsx
import React, { useEffect, useState } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { supabase } from '../../supabaseClient';
import { usePermissions } from '../../utils/permissions';
import {
  Box,
  Container,
  Typography,
  Button,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  CircularProgress,
  Alert,
  Chip,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
  InputAdornment,
  Stack,
  Divider,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Avatar,
  Tooltip,
  Fab,
  Card,
  CardContent,
  Grid,
  FormControlLabel,
  Switch,
  Snackbar,
  Breadcrumbs,
  Link as MuiLink,
  useTheme,
  alpha,
  Fade,
  LinearProgress,
  Tabs,
  Tab
} from '@mui/material';
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
  LocationCity as LocationCityIcon,
  Business as BusinessIcon,
  Warehouse as WarehouseIcon,
  Store as StoreIcon,
  Factory as FactoryIcon,
  Home as HomeIcon,
  Phone as PhoneIcon,
  Email as EmailIcon,
  Search as SearchIcon,
  FilterList as FilterListIcon,
  Close as CloseIcon,
  FiberManualRecord as FiberManualRecordIcon,
  LocalShipping as LocalShippingIcon,
  Info as InfoIcon,
  Inventory as InventoryIcon,
  Settings as SettingsIcon
} from '@mui/icons-material';
import ProductCapacitiesTab from './components/ProductCapacitiesTab';

const facilityTypes = [
  { value: 'warehouse', label: 'Warehouse', icon: <WarehouseIcon /> },
  { value: 'retail', label: 'Retail Store', icon: <StoreIcon /> },
  { value: 'office', label: 'Office', icon: <BusinessIcon /> },
  { value: 'manufacturing', label: 'Manufacturing', icon: <FactoryIcon /> },
  { value: 'distribution', label: 'Distribution Center', icon: <WarehouseIcon /> },
  { value: 'other', label: 'Other', icon: <HomeIcon /> }
];

const areaUnits = [
  { value: 'sq ft', label: 'Square Feet (sq ft)' },
  { value: 'sq m', label: 'Square Meters (sq m)' },
  { value: 'sq yd', label: 'Square Yards (sq yd)' },
  { value: 'acres', label: 'Acres' },
  { value: 'hectares', label: 'Hectares' }
];

const LocationsPage = () => {
  const { companyId } = useParams();
  const navigate = useNavigate();
  const theme = useTheme();
  const { canAccessPage, canEditPage, hasPermission, isManagerOrAbove, userRole, hierarchyLevel } = usePermissions();
  const [locations, setLocations] = useState([]);
  const [filteredLocations, setFilteredLocations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [companyName, setCompanyName] = useState('');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [detailDialogOpen, setDetailDialogOpen] = useState(false);
  const [selectedLocation, setSelectedLocation] = useState(null);
  const [editMode, setEditMode] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterType, setFilterType] = useState('all');
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success'
  });
  const [currentLocationId, setCurrentLocationId] = useState(null);
  const [detailsOpen, setDetailsOpen] = useState(false);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [locationToDelete, setLocationToDelete] = useState(null);
  
  // Additional data states for detail dialog
  const [locationCapacities, setLocationCapacities] = useState([]);
  
  // Form data for tabs when creating new location
  const [formCapacities, setFormCapacities] = useState([]);
  
  // Pagination state
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  
  const [formData, setFormData] = useState({
    id: '',
    name: '',
    address: '',
    city: '',
    state: '',
    zip_code: '',
    country: '',
    phone: '',
    email: '',
    facility_type: '',
    square_footage: '',
    square_footage_unit: 'sq ft',
    notes: '',
    is_main: false,
    is_active: true
  });

  // Tab state for form dialog
  const [activeTab, setActiveTab] = useState(0);

  const fetchLocations = async () => {
    setLoading(true);
    setError(null);
    try {
      const { data, error } = await supabase
        .from('locations')
        .select('*')
        .eq('company_id', companyId)
        .order('created_at', { ascending: false });

      if (error) throw error;
      
      // Sort to put main location first
      const sortedData = (data || []).sort((a, b) => {
        if (a.is_main && !b.is_main) return -1;
        if (!a.is_main && b.is_main) return 1;
        return new Date(b.created_at) - new Date(a.created_at);
      });

      setLocations(sortedData);
    } catch (err) {
      console.error('Error fetching locations:', err);
      setError('Failed to load locations. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const fetchCompanyData = async () => {
    try {
      const { data: companyData, error: companyError } = await supabase
        .from('companies')
        .select('name')
        .eq('id', companyId)
        .single();
      
      if (companyError) throw companyError;
      setCompanyName(companyData?.name || 'Company');
    } catch (err) {
      console.error('Error fetching company data:', err);
      setCompanyName('Company Name');
    }
  };

  // Define filter function
  const filterLocations = () => {
    let filtered = locations;

    if (searchTerm) {
      filtered = filtered.filter(location =>
        location.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        location.city?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        location.state?.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    if (filterType !== 'all') {
      filtered = filtered.filter(location => location.facility_type === filterType);
    }

    setFilteredLocations(filtered);
  };

  useEffect(() => {
    fetchLocations();
    fetchCompanyData();
  }, [companyId]);

  useEffect(() => {
    filterLocations();
  }, [locations, searchTerm, filterType]);

  // Permission check
  if (!hasPermission('locations.locations.view')) {
    return (
      <Container maxWidth="lg" sx={{ py: 8 }}>
        <Box sx={{ 
          display: 'flex', 
          flexDirection: 'column', 
          alignItems: 'center', 
          justifyContent: 'center',
          minHeight: '70vh',
          textAlign: 'center'
        }}>
          <Card sx={{
            p: 8,
            borderRadius: 3,
            border: `1px solid ${theme.palette.grey[400]}`,
            background: 'white',
            boxShadow: `0 12px 40px ${alpha(theme.palette.grey[900], 0.08)}`,
            maxWidth: 600,
            width: '100%',
            position: 'relative',
            overflow: 'hidden',
            cursor: 'default',
            transition: 'all 0.4s cubic-bezier(0.175, 0.885, 0.32, 1.275)',
            '&:hover': {
              transform: 'translateY(-8px)',
              boxShadow: `0 20px 60px ${alpha(theme.palette.grey[900], 0.15)}`,
              border: `1px solid ${alpha(theme.palette.primary.main, 0.3)}`
            },
            '&::before': {
              content: '""',
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              height: 4,
              background: `linear-gradient(90deg, ${theme.palette.primary.main}, ${theme.palette.grey[400]})`,
              opacity: 0.8,
              transition: 'opacity 0.3s ease'
            },
            '&:hover::before': {
              opacity: 1
            }
          }}>
            <Box sx={{ 
              p: 3, 
              borderRadius: 3, 
              background: `linear-gradient(${alpha(theme.palette.primary.main, 0.05)}, ${alpha(theme.palette.primary.main, 0.02)})`,
              border: `1px solid ${alpha(theme.palette.primary.main, 0.15)}`,
              mb: 6,
              display: 'inline-flex',
              boxShadow: `0 8px 24px ${alpha(theme.palette.primary.main, 0.15)}`
            }}>
              <LocationCityIcon sx={{ fontSize: 48, color: 'text.secondary' }} />
            </Box>
            <Typography variant="h3" sx={{ 
              mb: 3, 
              fontWeight: 500, 
              color: 'text.primary',
              fontSize: '2.5rem',
              letterSpacing: '-0.8px'
            }}>
              Access Restricted
            </Typography>
            <Typography variant="h6" sx={{ 
              color: 'text.secondary', 
              mb: 4,
              fontWeight: 500,
              fontSize: '1.2rem'
            }}>
              Locations Directory Permissions Required
            </Typography>
            <Typography variant="body1" sx={{ 
              color: 'text.secondary',
              lineHeight: 1.7,
              mb: 6,
              fontSize: '1.05rem',
              maxWidth: 450,
              mx: 'auto'
            }}>
              You don't currently have the necessary permissions to access the Locations Directory. 
              Contact your system administrator to request access or verify your role permissions.
            </Typography>
            <Button
              variant="outlined"
              onClick={() => navigate(-1)}
              sx={{
                borderRadius: 2,
                textTransform: 'none',
                fontWeight: 500,
                px: 4,
                py: 1.5,
                fontSize: '1rem'
              }}
            >
              Return to Previous Page
            </Button>
          </Card>
        </Box>
      </Container>
    );
  }
  
  // Check edit permissions  
  const canEdit = hasPermission('locations.locations.edit', 'write');

  const resetFormData = () => {
    setFormData({
      name: '',
      address: '',
      city: '',
      state: '',
      zip_code: '',
      country: '',
      phone: '',
      email: '',
      facility_type: '',
      square_footage: '',
      square_footage_unit: 'sq ft',
      notes: '',
      is_main: false,
      is_active: true
    });
    setActiveTab(0);
  };

  const handleOpenDialog = async (location = null) => {
    if (location) {
      setEditMode(true);
      setFormData({
        id: location.id,
        name: location.name || '',
        address: location.address || '',
        city: location.city || '',
        state: location.state || '',
        zip_code: location.zip_code || '',
        country: location.country || '',
        phone: location.phone || '',
        email: location.email || '',
        facility_type: location.facility_type || '',
        square_footage: location.square_footage || '',
        square_footage_unit: location.square_footage_unit || 'sq ft',
        notes: location.notes || '',
        is_main: location.is_main || false,
        is_active: location.is_active !== undefined ? location.is_active : true
      });
      
    } else {
      setEditMode(false);
      resetFormData();
    }
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setEditMode(false);
    resetFormData();
    setActiveTab(0);
    // Clear form tab data
    setFormCapacities([]);
  };

  const handleOpenDetailDialog = async (location) => {
    setSelectedLocation(location);
    setDetailDialogOpen(true);
    
    // Load additional data for this location
    await loadLocationAdditionalData(location.id);
  };


  const loadLocationAdditionalData = async (locationId) => {
    // Reset states
    setLocationCapacities([]);

    try {
      // Load product capacities
      try {
        const { data: capacities, error: capacitiesError } = await supabase
          .from('location_product_capacities')
          .select(`
            *,
            product:product_id(
              name,
              sku
            )
          `)
          .eq('company_id', companyId)
          .eq('location_id', locationId);

        if (capacitiesError && capacitiesError.code !== 'PGRST204') {
          console.error('Error loading capacities:', capacitiesError);
        } else {
          setLocationCapacities(capacities || []);
        }
      } catch (err) {
        console.error('Capacities table may not exist:', err);
      }

    } catch (err) {
      console.error('Error loading location additional data:', err);
    }
  };

  const handleCloseDetailDialog = () => {
    setDetailDialogOpen(false);
    setSelectedLocation(null);
    setLocationCapacities([]);
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleSubmit = async () => {
    if (!formData.name.trim()) {
      setError('Location name is required');
      return;
    }

    // Check permissions before proceeding
    if (!hasPermission('locations.locations.edit', 'write')) {
      setError('You do not have permission to create or edit locations.');
      return;
    }

    setSubmitting(true);
    setError(null);

    try {
      const locationData = {
        name: formData.name.trim(),
        address: formData.address.trim() || null,
        city: formData.city.trim() || null,
        state: formData.state.trim() || null,
        zip_code: formData.zip_code.trim() || null,
        country: formData.country.trim() || null,
        phone: formData.phone.trim() || null,
        email: formData.email.trim() || null,
        facility_type: formData.facility_type || null,
        square_footage: formData.square_footage ? parseInt(formData.square_footage) : null,
        square_footage_unit: formData.square_footage_unit || 'sq ft',
        is_main: formData.is_main,
        is_active: formData.is_active,
        notes: formData.notes.trim() || null,
        company_id: companyId
      };

      // If setting this location as main, unset all other main locations first
      if (formData.is_main) {
        await supabase
          .from('locations')
          .update({ is_main: false })
          .eq('company_id', companyId)
          .neq('id', formData.id || '00000000-0000-0000-0000-000000000000');
      }

      let result;
      if (editMode) {
        result = await supabase
          .from('locations')
          .update({ ...locationData, updated_at: new Date().toISOString() })
          .eq('id', formData.id)
          .select()
          .single();
      } else {
        result = await supabase
          .from('locations')
          .insert(locationData)
          .select()
          .single();
      }

      if (result.error) throw result.error;

      const newLocationId = editMode ? formData.id : result.data.id;

      // Save additional data for both new and existing locations
      if (true) {
        // Save capacities if any
        if (formCapacities.length > 0) {
          const capacitiesData = formCapacities.map(cap => {
            const { id, ...cleanCap } = cap; // Remove temp id
            return {
              ...cleanCap,
              company_id: companyId,
              location_id: newLocationId
            };
          });
          
          const { error: capacitiesError } = await supabase
            .from('location_product_capacities')
            .insert(capacitiesData);
            
          if (capacitiesError) console.error('Error saving capacities:', capacitiesError);
        }

      }

      // Refresh the locations list to reflect main location changes and proper sorting
      await fetchLocations();

      handleCloseDialog();
    } catch (err) {
      console.error('Error saving location:', err);
      setError(`Failed to ${editMode ? 'update' : 'create'} location. Please try again.`);
    } finally {
      setSubmitting(false);
    }
  };

  const handleDeleteClick = (locationId, locationName) => {
    setLocationToDelete({ id: locationId, name: locationName });
    setDeleteConfirmOpen(true);
  };

  const handleConfirmDelete = async () => {
    if (!locationToDelete) return;

    // Check permissions before proceeding
    if (!hasPermission('locations.locations.delete', 'delete')) {
      setSnackbar({
        open: true,
        message: 'You do not have permission to delete locations.',
        severity: 'error'
      });
      setDeleteConfirmOpen(false);
      setLocationToDelete(null);
      return;
    }

    try {
      const { error } = await supabase
        .from('locations')
        .delete()
        .eq('id', locationToDelete.id);

      if (error) throw error;

      setLocations(prev => prev.filter(location => location.id !== locationToDelete.id));
    } catch (err) {
      setSnackbar({
        open: true,
        message: `Error deleting location: ${err.message}`,
        severity: 'error'
      });
    }

    setDeleteConfirmOpen(false);
    setLocationToDelete(null);
  };

  const handleCancelDelete = () => {
    setDeleteConfirmOpen(false);
    setLocationToDelete(null);
  };

  const getFacilityIcon = (type) => {
    const facility = facilityTypes.find(f => f.value === type);
    return facility ? facility.icon : <LocationCityIcon />;
  };

  const getFacilityLabel = (type) => {
    const facility = facilityTypes.find(f => f.value === type);
    return facility ? facility.label : 'Unknown';
  };

  const formatAddress = (location) => {
    const parts = [
      location.address,
      location.city,
      location.state,
      location.zip_code
    ].filter(Boolean);
    return parts.join(', ');
  };

  const formatFullAddress = (location) => {
    const addressLines = [];
    if (location.address) addressLines.push(location.address);
    
    const cityStateZip = [location.city, location.state, location.zip_code].filter(Boolean).join(', ');
    if (cityStateZip) addressLines.push(cityStateZip);
    
    if (location.country) addressLines.push(location.country);
    
    return addressLines;
  };

  const handleCloseSnackbar = () => {
    setSnackbar(prev => ({ ...prev, open: false }));
  };

  // Pagination handlers
  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  if (loading) {
    return (
      <Container disableGutters maxWidth="xl" sx={{ px: 3, py: 4, pb: 16 }}>
        <Card sx={{ 
          borderRadius: 3,
          mt: 10,
          mx: 'auto',
          maxWidth: 600,
          border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
          background: `linear-gradient(135deg, ${alpha(theme.palette.grey[100], 0.8)}, ${alpha(theme.palette.grey[50], 0.9)})`,
          boxShadow: `0 12px 32px ${alpha('#3F51B5', 0.15)}`,
          transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
          '&:hover': {
            transform: 'translateY(-2px)',
            boxShadow: `0 16px 40px ${alpha('#3F51B5', 0.2)}`
          }
        }}>
          <CardContent sx={{ p: 6, textAlign: 'center' }}>
            <Box sx={{ mb: 3 }}>
              <Box sx={{
                display: 'inline-flex',
                p: 3,
                borderRadius: '50%',
                bgcolor: alpha('#3F51B5', 0.1),
                mb: 2
              }}>
                <LocationCityIcon sx={{ fontSize: 48, color: '#3F51B5' }} />
              </Box>
            </Box>
            <Typography variant="h6" sx={{
              fontWeight: 500,
              color: '#3F51B5',
              mb: 1,
              fontSize: '1.1rem'
            }}>
              Loading Business Locations
            </Typography>
            <Typography variant="body2" sx={{ 
              color: 'text.secondary',
              fontWeight: 500,
              maxWidth: 400,
              mx: 'auto',
              lineHeight: 1.6,
              mb: 3
            }}>
              Analyzing location data and facility information...
            </Typography>
            <Box sx={{ 
              width: '60%', 
              mx: 'auto',
              '& .MuiLinearProgress-root': {
                height: 8,
                borderRadius: 4,
                bgcolor: alpha(theme.palette.grey[300], 0.3),
                '& .MuiLinearProgress-bar': {
                  bgcolor: '#3F51B5',
                  borderRadius: 4
                }
              }
            }}>
              <LinearProgress />
            </Box>
          </CardContent>
        </Card>
      </Container>
    );
  }

  return (
    <Fade in timeout={800}>
      <Box>
        <Container maxWidth="xl" sx={{ py: 3 }}>
      {/* Header Section */}
      <Box sx={{ mb: 3 }}>
        {/* Enhanced Page Header */}
        <Card sx={{
          borderRadius: 3,
          mt: 5,
          mb: 3,
          border: `1px solid ${theme.palette.grey[400]}`,
          borderTop: '3px solid #3F51B5',
          background: theme.palette.grey[100],
          backdropFilter: 'blur(20px)',
          boxShadow: 'none',
          '&:hover': {
            transform: 'translateY(-2px)',
            boxShadow: `0 6px 20px ${alpha('#3F51B5', 0.15)}`,
          },
          transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
        }}>
          <CardContent sx={{ p: 3 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2.5, mb: 2 }}>
              <Box sx={{
                p: 2,
                borderRadius: 3,
                background: `linear-gradient(${alpha('#3F51B5', 0.08)}, ${alpha('#3F51B5', 0.04)})`,
                border: `1px solid ${alpha('#3F51B5', 0.15)}`
              }}>
                <LocationCityIcon sx={{ fontSize: 32, color: '#3F51B5' }} />
              </Box>
              <Box sx={{ flex: 1 }}>
                <Typography variant="h4" sx={{
                  fontWeight: 500,
                  color: '#3F51B5',
                  mb: 1,
                  fontSize: '2rem',
                  letterSpacing: '-0.5px'
                }}>
                  Business Locations
                </Typography>
                <Typography variant="subtitle1" sx={{ 
                  color: 'text.secondary', 
                  fontWeight: 500,
                  fontSize: '1rem'
                }}>
                  {companyName}
                </Typography>
              </Box>
            </Box>
            <Typography variant="body2" sx={{ 
              color: 'text.secondary', 
              maxWidth: 700, 
              lineHeight: 1.6,
              fontSize: '0.95rem',
              fontWeight: 500
            }}>
              Manage your facilities and business locations, track addresses, and organize your operational sites 
              with comprehensive location management and contact tracking.
            </Typography>
          </CardContent>
        </Card>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      <Card sx={{
        height: 100,    // optional, for vertical size 
        mb: 2,
        border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
        borderRadius: 3,
        background: `linear-gradient(135deg, ${alpha(theme.palette.grey[100], 0.8)}, ${alpha(theme.palette.grey[50], 0.9)})`,
        boxShadow: `0 8px 24px ${alpha(theme.palette.grey[500], 0.12)}`,
        transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
        animation: 'slideInUp 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
        '@keyframes slideInUp': {
          '0%': {
            transform: 'translateY(100%)',
            opacity: 0,
          },
          '100%': {
            transform: 'translateY(0)',
            opacity: 1,
          }
        },
        '&:hover': {
          transform: 'translateY(-2px)',
          boxShadow: `0 20px 40px ${alpha(theme.palette.primary.main, 0.25)}`,
          border: `1px solid ${alpha(theme.palette.primary.main, 0.3)}`
        }
      }}>
        <CardContent>
          <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'center', flexWrap: 'wrap' }}>
            <TextField
              placeholder="Search locations..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              size="small"
              sx={{ 
                minWidth: 280,
                mt: 0.5,
                '& .MuiOutlinedInput-root': {
                  borderRadius: 3,
                  border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
                  background: 'white',
                  '& fieldset': { border: 'none' },
                  '&:hover': {
    
                    boxShadow: `0 4px 12px ${alpha(theme.palette.primary.main, 0.15)}`
                  },
                  '&.Mui-focused': {
    
                    boxShadow: `0 4px 16px ${alpha(theme.palette.primary.main, 0.25)}`
                  }
                }
              }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon sx={{ color: theme.palette.primary.main }} />
                  </InputAdornment>
                ),
                sx: {
                  fontSize: '0.9rem',
                  fontWeight: 500
                }
              }}
            />
            <FormControl size="small" sx={{ minWidth: 180 }}>
              <InputLabel sx={{ 
                fontSize: '0.85rem', 
                fontWeight: 500,
                mt: 0.5,
                transform: 'translate(14px, 1px) scale(1)',
                '&.Mui-focused, &.MuiFormLabel-filled': {
                  transform: 'translate(14px, -17px) scale(0.75)'
                }
              }}>
                Filter by Type
              </InputLabel>
              <Select
                value={filterType}
                label="Filter by Type"
                onChange={(e) => setFilterType(e.target.value)}
                sx={{
                  borderRadius: 3,
                  mt: 0.5,
                  border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
                  background: 'white',
                  '& .MuiSelect-select': {
                    fontSize: '0.9rem',
                    fontWeight: 500
                  },
                  '& .MuiOutlinedInput-notchedOutline': { border: 'none' },
                  '&:hover': {
    
                    boxShadow: `0 2px 8px ${alpha(theme.palette.primary.main, 0.15)}`
                  },
                  '&.Mui-focused': {
    
                    boxShadow: `0 2px 8px ${alpha(theme.palette.primary.main, 0.25)}`
                  }
                }}
              >
                <MenuItem value="all" sx={{ fontSize: '0.9rem', fontWeight: 500 }}>All Types</MenuItem>
                {facilityTypes.map((type) => (
                  <MenuItem key={type.value} value={type.value} sx={{ fontSize: '0.9rem', fontWeight: 500 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      {type.icon}
                      {type.label}
                    </Box>
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <Box sx={{ flex: 1 }} />
            <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500 }}>
              {filteredLocations.length} location{filteredLocations.length !== 1 ? 's' : ''}
            </Typography>
          </Box>
        </CardContent>
      </Card>

      {filteredLocations.length === 0 ? (
        <Paper 
          sx={{
            textAlign: 'center',
            py: 8,
            px: 4,
            border: '2px dashed',
            borderColor: 'divider',
            borderRadius: 2,
          }}
        >
          <Box sx={{ 
            p: 2.5, 
            borderRadius: 3, 
            background: `linear-gradient(${alpha(theme.palette.primary.main, 0.1)}, ${alpha(theme.palette.primary.main, 0.05)})`,
            border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`,
            backdropFilter: 'blur(10px)',
            boxShadow: `0 8px 24px ${alpha(theme.palette.primary.main, 0.3)}`,
            mb: 3,
            width: 'fit-content',
            mx: 'auto'
          }}>
            <LocationCityIcon sx={{ fontSize: 36, color: 'text.secondary' }} />
          </Box>
          <Typography variant="h6" gutterBottom sx={{ fontWeight: 500 }}>
            {locations.length === 0 ? 'No Locations Yet' : 'No Matching Locations'}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3, maxWidth: 400, mx: 'auto', fontWeight: 500 }}>
            {locations.length === 0 
              ? 'Add your first business location to start organizing your facilities and operations.'
              : 'Try adjusting your search or filter criteria.'}
          </Typography>
          {locations.length === 0 && canEdit && (
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => handleOpenDialog()}
              sx={{ 
                borderRadius: 3,
                px: 4,
                py: 1.5,
                fontWeight: 500,
                fontSize: '0.9rem',
                border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
                boxShadow: `0 6px 18px ${alpha(theme.palette.primary.main, 0.25)}`,
                transition: 'all 0.2s cubic-bezier(0.4, 0.0, 0.2, 1)',
                '&:hover': {
                  transform: 'translateY(-1px) scale(1.02)',
                  boxShadow: `0 8px 24px ${alpha(theme.palette.primary.main, 0.35)}`
                }
              }}
            >
              Add Your First Location
            </Button>
          )}
        </Paper>
      ) : (
        <Paper sx={{ 
          borderRadius: 3, 
          border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
          background: `linear-gradient(135deg, ${alpha(theme.palette.grey[100], 0.8)}, ${alpha(theme.palette.grey[50], 0.9)})`,
          boxShadow: `0 12px 32px ${alpha(theme.palette.primary.main, 0.15)}`,
          overflow: 'hidden',
          transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
          animation: 'slideInUp 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
          '@keyframes slideInUp': {
            '0%': {
              transform: 'translateY(100%)',
              opacity: 0,
            },
            '100%': {
              transform: 'translateY(0)',
              opacity: 1,
            }
          },
          '&:hover': {
            transform: 'translateY(-2px)',
            boxShadow: `0 20px 40px ${alpha(theme.palette.primary.main, 0.25)}`,
            border: `1px solid ${alpha(theme.palette.primary.main, 0.3)}`
          }
        }}>
          <TableContainer sx={{ overflow: 'visible' }}>
            <Table stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell 
                    sx={{ 
                      fontWeight: 500,
                      fontSize: '0.9rem',
                      bgcolor: 'white',
                      borderBottom: `2px solid ${theme.palette.primary.main}`,
                      color: 'primary.main',
                      py: 1.5,
                      px: 2.5
                    }}
                  >
                    Location Name
                  </TableCell>
                  <TableCell 
                    sx={{ 
                      fontWeight: 500,
                      fontSize: '0.9rem',
                      bgcolor: 'white',
                      borderBottom: `2px solid ${theme.palette.primary.main}`,
                      color: 'primary.main',
                      py: 1.5,
                      px: 2.5
                    }}
                  >
                    Type
                  </TableCell>
                  <TableCell 
                    sx={{ 
                      fontWeight: 500,
                      fontSize: '0.9rem',
                      bgcolor: 'white',
                      borderBottom: `2px solid ${theme.palette.primary.main}`,
                      color: 'primary.main',
                      py: 1.5,
                      px: 2.5
                    }}
                  >
                    Address
                  </TableCell>
                  <TableCell 
                    sx={{ 
                      fontWeight: 500,
                      fontSize: '0.9rem',
                      bgcolor: 'white',
                      borderBottom: `2px solid ${theme.palette.primary.main}`,
                      color: 'primary.main',
                      py: 1.5,
                      px: 2.5
                    }}
                  >
                    Phone
                  </TableCell>
                  <TableCell 
                    sx={{ 
                      fontWeight: 500,
                      fontSize: '0.9rem',
                      bgcolor: 'white',
                      borderBottom: `2px solid ${theme.palette.primary.main}`,
                      color: 'primary.main',
                      py: 1.5,
                      px: 2.5
                    }}
                  >
                    Status
                  </TableCell>
                  <TableCell 
                    align="center"
                    sx={{ 
                      fontWeight: 500,
                      fontSize: '0.9rem',
                      bgcolor: 'white',
                      borderBottom: `2px solid ${theme.palette.primary.main}`,
                      color: 'primary.main',
                      py: 1.5,
                      px: 2.5,
                      width: 120
                    }}
                  >
                    Actions
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {(rowsPerPage > 0
                  ? filteredLocations.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                  : filteredLocations
                ).map((location, index) => (
                  <TableRow 
                    key={location.id} 
                    hover
                    onClick={() => handleOpenDetailDialog(location)}
                    sx={{ 
                      cursor: 'pointer',
                      transition: 'all 0.2s ease',
                      '&:hover': { 
                        bgcolor: 'grey.50',
                        transform: 'translateX(4px)',
                        transition: 'all 0.2s ease',
                        boxShadow: 'inset 4px 0 0 black'
                      },
                      '& .MuiTableCell-root': {
                        borderBottom: `1px solid ${alpha(theme.palette.grey[300], 0.6)}`,
                        py: 2,
                        px: 2.5
                      }
                    }}
                  >
                    {/* Location Name */}
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Box>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Typography 
                              variant="body1" 
                              sx={{ 
                                fontWeight: 500,
                                color: 'text.primary',
                                fontSize: '0.95rem'
                              }}
                            >
                              {location.name}
                            </Typography>
                            {location.is_main && (
                              <Chip 
                                label="Main" 
                                size="small" 
                                color="primary" 
                                sx={{ height: 20, fontSize: '0.7rem' }}
                              />
                            )}
                          </Box>
                        </Box>
                      </Box>
                    </TableCell>
                    
                    {/* Type */}
                    <TableCell>
                      <Chip 
                        label={getFacilityLabel(location.facility_type)}
                        size="small"
                        color="primary"
                        variant="outlined"
                        sx={{ 
                          fontSize: '0.75rem',
                          fontWeight: 500,
                          borderRadius: 2
                        }}
                      />
                    </TableCell>
                    
                    {/* Address */}
                    <TableCell>
                      <Typography 
                        variant="body2" 
                        sx={{ 
                          fontWeight: 500,
                          color: 'text.primary',
                          fontSize: '0.9rem'
                        }}
                      >
                        {formatAddress(location) || 'No address provided'}
                      </Typography>
                    </TableCell>
                    
                    {/* Phone */}
                    <TableCell>
                      <Typography 
                        variant="body2" 
                        sx={{ 
                          fontWeight: 500,
                          color: 'text.primary',
                          fontSize: '0.9rem'
                        }}
                      >
                        {location.phone || '—'}
                      </Typography>
                    </TableCell>
                    
                    {/* Status */}
                    <TableCell>
                      <Chip 
                        label={location.is_active ? 'Active' : 'Inactive'}
                        size="small"
                        color={location.is_active ? 'success' : 'error'}
                        variant="outlined"
                      />
                    </TableCell>
                    
                    {/* Actions */}
                    <TableCell align="center">
                      {canEdit && (
                        <Tooltip title="Edit">
                          <IconButton 
                          size="small" 
                          onClick={(e) => {
                            e.stopPropagation();
                            handleOpenDialog(location);
                          }}
                          sx={{
                            mr: 1,
                            color: 'text.primary',
                            border: `1px solid ${alpha(theme.palette.grey[400], 0.3)}`,
                            borderRadius: 2,
                            transition: 'all 0.2s cubic-bezier(0.4, 0.0, 0.2, 1)',
                            '&:hover': {
                              bgcolor: 'primary.main',
                              color: 'white',
                              transform: 'scale(1.05)',
                              boxShadow: `0 4px 12px ${alpha(theme.palette.primary.main, 0.3)}`
                            }
                          }}
                        >
                          <EditIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      )}
                      {hasPermission('locations.locations.delete', 'delete') && (
                        <Tooltip title="Delete">
                          <IconButton 
                          size="small" 
                          onClick={(e) => {
                            e.stopPropagation();
                            handleDeleteClick(location.id, location.name);
                          }}
                          sx={{
                            color: 'error.main',
                            border: `1px solid ${alpha(theme.palette.error.main, 0.3)}`,
                            borderRadius: 2,
                            transition: 'all 0.2s cubic-bezier(0.4, 0.0, 0.2, 1)',
                            '&:hover': {
                              bgcolor: 'error.main',
                              color: 'white',
                              transform: 'scale(1.05)',
                              boxShadow: `0 4px 12px ${alpha(theme.palette.error.main, 0.3)}`
                            }
                          }}
                        >
                          <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
          
          <Box sx={{ 
            borderTop: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
            background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.05)}, ${alpha(theme.palette.primary.main, 0.02)})`,
            borderRadius: '0 0 12px 12px'
          }}>
            <TablePagination
              rowsPerPageOptions={[5, 10, 25]}
              component="div"
              count={filteredLocations.length}
              rowsPerPage={rowsPerPage}
              page={page}
              onPageChange={handleChangePage}
              onRowsPerPageChange={handleChangeRowsPerPage}
              sx={{ 
                px: 3,
                py: 2,
                '& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows': {
                  fontSize: '0.9rem',
                  fontWeight: 500,
                  color: theme.palette.text.primary
                },
                '& .MuiTablePagination-select': {
                  border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
                  borderRadius: 2,
                  px: 2,
                  py: 1,
                  fontWeight: 500,
                  fontSize: '0.9rem',
                  background: 'white',
                  '&:hover': {
    
                    boxShadow: `0 2px 8px ${alpha(theme.palette.primary.main, 0.15)}`
                  }
                },
                '& .MuiTablePagination-actions': {
                  '& .MuiIconButton-root': {
                    border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
                    borderRadius: 2,
                    margin: '0 2px',
                    background: 'white',
                    color: theme.palette.primary.main,
                    fontWeight: 500,
                    transition: 'all 0.2s ease',
                    '&:hover': {
                      background: theme.palette.primary.main,
                      color: 'white',
                      transform: 'translateY(-1px)',
      
                      boxShadow: `0 2px 8px ${alpha(theme.palette.primary.main, 0.25)}`
                    },
                    '&.Mui-disabled': {
                      background: alpha(theme.palette.grey[300], 0.5),
                      color: theme.palette.grey[500],
                      border: `1px solid ${theme.palette.grey[300]}`,
                      transform: 'none',
                      boxShadow: 'none'
                    }
                  }
                }
              }}
            />
          </Box>
        </Paper>
      )}

      {canEdit && (
        <Fab
          color="primary"
          aria-label="add"
          sx={{
          position: 'fixed',
          bottom: 24,
          right: 24,
          width: 100,
          height: 100,
          border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
          boxShadow: `0 6px 18px ${alpha(theme.palette.primary.main, 0.25)}`,
          transition: 'all 0.2s cubic-bezier(0.4, 0.0, 0.2, 1)',
          '&:hover': {
            transform: 'translateY(-1px) scale(1.02)',
            boxShadow: `0 8px 24px ${alpha(theme.palette.primary.main, 0.35)}`
          }
        }}
        onClick={() => handleOpenDialog()}
        >
          <AddIcon sx={{ fontSize: 32 }} />
        </Fab>
      )}

      {/* Detail Dialog */}
      <Dialog 
        open={detailDialogOpen} 
        onClose={handleCloseDetailDialog} 
        maxWidth="lg" 
        fullWidth
        PaperProps={{
          sx: { 
            borderRadius: 4,
            border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
            maxHeight: '90vh',
            boxShadow: '0 24px 48px rgba(0,0,0,0.15)',
            bgcolor: alpha(theme.palette.grey[50], 0.95),
            backdropFilter: 'blur(10px)',
            overflow: 'hidden'
          }
        }}
      >
        {selectedLocation && (
          <>
            <DialogTitle sx={{ 
              pb: 1.5, 
              pt: 2.5,
              px: 2.5,
              fontSize: '1.25rem', 
              fontWeight: 500,
              color: 'text.primary',
              borderBottom: `1px solid ${alpha(theme.palette.grey[300], 0.8)}`,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              bgcolor: 'white'
            }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Box sx={{
                  p: 1.5,
                  borderRadius: 2,
                  bgcolor: alpha(theme.palette.primary.main, 0.1),
                  border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`
                }}>
                  {getFacilityIcon(selectedLocation.facility_type)}
                </Box>
                <Box>
                  <Typography variant="h6" component="span" sx={{ fontWeight: 500, fontSize: '1.1rem', color: 'text.primary' }}>
                    Location Details
                  </Typography>
                  <Typography variant="body2" sx={{ color: 'text.secondary', fontWeight: 500, mt: 0.5 }}>
                    {selectedLocation.name} - {getFacilityLabel(selectedLocation.facility_type)}
                  </Typography>
                </Box>
              </Box>
              <IconButton 
                onClick={handleCloseDetailDialog} 
                size="small"
                sx={{ 
                  color: 'text.secondary',
                  '&:hover': {
                    bgcolor: alpha(theme.palette.grey[400], 0.1),
                    color: 'text.primary'
                  }
                }}
              >
                <CloseIcon />
              </IconButton>
            </DialogTitle>

            {/* Content Section */}
            <DialogContent sx={{ p: 0 }}>
              <Box sx={{ p: 3 }}>
                <Grid container spacing={3}>
                  {/* Address Section */}
                  <Grid item xs={12} md={6}>
                    <Card sx={{ 
                      p: 3, 
                      height: '100%',
                      borderRadius: 3,
                      border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
                      background: 'white',
                      minHeight: '220px',
                      transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                      cursor: 'default',
                      '&:hover': {
                        transform: 'translateY(-1px)',
                        boxShadow: `0 16px 48px ${alpha(theme.palette.primary.main, 0.15)}`,
                        border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`
                      }
                    }}>
                      <Typography variant="h6" sx={{ fontWeight: 500, mb: 3, color: 'primary.main' }}>
                        Address Information
                      </Typography>
                      {formatFullAddress(selectedLocation).length > 0 ? (
                        <Stack spacing={0.5}>
                          {formatFullAddress(selectedLocation).map((line, index) => (
                            <Typography key={index} variant="body1" sx={{ lineHeight: 1.5, fontWeight: 500 }}>
                              {line}
                            </Typography>
                          ))}
                        </Stack>
                      ) : (
                        <Typography variant="body1" sx={{ fontWeight: 500, color: 'text.secondary', fontStyle: 'italic' }}>
                          No address provided
                        </Typography>
                      )}
                    </Card>
                  </Grid>
                  
                  {/* Contact Section */}
                  <Grid item xs={12} md={6}>
                    <Card sx={{ 
                      p: 3, 
                      height: '100%',
                      borderRadius: 3,
                      border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
                      background: 'white',
                      minHeight: '220px',
                      transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                      cursor: 'default',
                      '&:hover': {
                        transform: 'translateY(-1px)',
                        boxShadow: `0 16px 48px ${alpha(theme.palette.primary.main, 0.15)}`,
                        border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`
                      }
                    }}>
                      <Typography variant="h6" sx={{ fontWeight: 500, mb: 3, color: 'primary.main' }}>
                        Contact Information
                      </Typography>
                      <Stack spacing={3}>
                        {selectedLocation.phone && (
                          <Box sx={{ mb: 2 }}>
                            <Typography variant="subtitle2" sx={{ fontWeight: 500, color: 'text.secondary', mb: 0.5 }}>
                              Phone
                            </Typography>
                            <Typography variant="body1" sx={{ fontWeight: 500 }}>
                              {selectedLocation.phone}
                            </Typography>
                          </Box>
                        )}
                        {selectedLocation.email && (
                          <Box sx={{ mb: 2 }}>
                            <Typography variant="subtitle2" sx={{ fontWeight: 500, color: 'text.secondary', mb: 0.5 }}>
                              Email
                            </Typography>
                            <Typography variant="body1" sx={{ fontWeight: 500 }}>
                              {selectedLocation.email}
                            </Typography>
                          </Box>
                        )}
                        {!selectedLocation.phone && !selectedLocation.email && (
                          <Typography variant="body1" sx={{ fontWeight: 500, color: 'text.secondary', fontStyle: 'italic' }}>
                            No contact information provided
                          </Typography>
                        )}
                      </Stack>
                    </Card>
                  </Grid>

                  {/* Location Status & Type */}
                  <Grid item xs={12}>
                    <Card sx={{ 
                      borderRadius: 3,
                      border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
                      background: 'white',
                      transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                      cursor: 'default',
                      '&:hover': {
                        transform: 'translateY(-1px)',
                        boxShadow: `0 16px 48px ${alpha(theme.palette.primary.main, 0.15)}`,
                        border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`
                      }
                    }}>
                      <CardContent sx={{ p: 4 }}>
                        <Typography variant="h6" sx={{ fontWeight: 500, mb: 3, color: 'primary.main' }}>
                          Location Details
                        </Typography>
                        <Grid container spacing={3}>
                          <Grid item xs={12} md={3}>
                            <Box sx={{ mb: 2 }}>
                              <Typography variant="subtitle2" sx={{ fontWeight: 500, color: 'text.secondary', mb: 0.5 }}>
                                Facility Type
                              </Typography>
                              <Typography variant="body1" sx={{ fontWeight: 500 }}>
                                {getFacilityLabel(selectedLocation.facility_type)}
                              </Typography>
                            </Box>
                          </Grid>
                          <Grid item xs={12} md={3}>
                            <Box sx={{ mb: 2 }}>
                              <Typography variant="subtitle2" sx={{ fontWeight: 500, color: 'text.secondary', mb: 0.5 }}>
                                Status
                              </Typography>
                              <Typography variant="body1" sx={{ fontWeight: 500 }}>
                                {selectedLocation.is_active ? 'Active' : 'Inactive'}
                              </Typography>
                            </Box>
                          </Grid>
                          <Grid item xs={12} md={3}>
                            <Box sx={{ mb: 2 }}>
                              <Typography variant="subtitle2" sx={{ fontWeight: 500, color: 'text.secondary', mb: 0.5 }}>
                                Location Type
                              </Typography>
                              <Typography variant="body1" sx={{ fontWeight: 500 }}>
                                {selectedLocation.is_main ? 'Main Location' : 'Secondary Location'}
                              </Typography>
                            </Box>
                          </Grid>
                          <Grid item xs={12} md={3}>
                            <Box sx={{ mb: 2 }}>
                              <Typography variant="subtitle2" sx={{ fontWeight: 500, color: 'text.secondary', mb: 0.5 }}>
                                Square Footage
                              </Typography>
                              <Typography variant="body1" sx={{ fontWeight: 500 }}>
                                {selectedLocation.square_footage ? `${selectedLocation.square_footage?.toLocaleString()} ${selectedLocation.square_footage_unit || 'sq ft'}` : 'Not specified'}
                              </Typography>
                            </Box>
                          </Grid>
                        </Grid>
                      </CardContent>
                    </Card>
                  </Grid>

                  {/* Product Capacities Section */}
                  <Grid item xs={12}>
                    <Card sx={{ 
                      borderRadius: 3,
                      border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
                      background: 'white',
                      transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                      cursor: 'default',
                      '&:hover': {
                        transform: 'translateY(-2px)',
                        boxShadow: `0 16px 48px ${alpha(theme.palette.primary.main, 0.15)}`,
                        border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`
                      }
                    }}>
                      <CardContent sx={{ p: 4 }}>
                        <Typography variant="h6" sx={{ fontWeight: 500, mb: 3, color: 'primary.main' }}>
                          Product Capacities
                        </Typography>
                        {locationCapacities.length > 0 ? (
                          <TableContainer sx={{ 
                            borderRadius: 3,
                            border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
                            background: `linear-gradient(135deg, ${alpha(theme.palette.grey[100], 0.8)}, ${alpha(theme.palette.grey[50], 0.9)})`,
                            boxShadow: `0 12px 32px ${alpha(theme.palette.primary.main, 0.15)}`,
                            overflow: 'hidden'
                          }}>
                            <Table stickyHeader>
                              <TableHead>
                                <TableRow>
                                  <TableCell sx={{ 
                                    fontWeight: 500,
                                    fontSize: '0.9rem',
                                    bgcolor: 'white',
                                    borderBottom: `2px solid ${theme.palette.primary.main}`,
                                    color: 'primary.main',
                                    py: 1.5,
                                    px: 2.5
                                  }}>
                                    Product
                                  </TableCell>
                                  <TableCell sx={{ 
                                    fontWeight: 500,
                                    fontSize: '0.9rem',
                                    bgcolor: 'white',
                                    borderBottom: `2px solid ${theme.palette.primary.main}`,
                                    color: 'primary.main',
                                    py: 1.5,
                                    px: 2.5
                                  }}>
                                    Capacity
                                  </TableCell>
                                  <TableCell sx={{ 
                                    fontWeight: 500,
                                    fontSize: '0.9rem',
                                    bgcolor: 'white',
                                    borderBottom: `2px solid ${theme.palette.primary.main}`,
                                    color: 'primary.main',
                                    py: 1.5,
                                    px: 2.5
                                  }}>
                                    Setup Time
                                  </TableCell>
                                  <TableCell sx={{ 
                                    fontWeight: 500,
                                    fontSize: '0.9rem',
                                    bgcolor: 'white',
                                    borderBottom: `2px solid ${theme.palette.primary.main}`,
                                    color: 'primary.main',
                                    py: 1.5,
                                    px: 2.5
                                  }}>
                                    Cleanup Time
                                  </TableCell>
                                </TableRow>
                              </TableHead>
                              <TableBody>
                                {locationCapacities.map((capacity) => (
                                  <TableRow 
                                    key={capacity.id} 
                                    hover
                                    sx={{ 
                                      cursor: 'default',
                                      transition: 'all 0.2s ease',
                                      '&:hover': { 
                                        bgcolor: 'grey.50',
                                        transform: 'translateX(4px)',
                                        transition: 'all 0.2s ease',
                                        boxShadow: 'inset 4px 0 0 black'
                                      },
                                      '& .MuiTableCell-root': {
                                        borderBottom: `1px solid ${alpha(theme.palette.grey[300], 0.6)}`,
                                        py: 2,
                                        px: 2.5
                                      }
                                    }}
                                  >
                                    <TableCell>
                                      <Typography variant="body1" sx={{ 
                                        fontWeight: 500,
                                        color: 'text.primary',
                                        fontSize: '0.95rem'
                                      }}>
                                        {capacity.product?.name || 'Unknown Product'}
                                      </Typography>
                                      {capacity.product?.sku && (
                                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                                          SKU: {capacity.product.sku}
                                        </Typography>
                                      )}
                                    </TableCell>
                                    <TableCell>
                                      <Typography variant="body2" sx={{ 
                                        fontWeight: 500,
                                        color: 'text.primary',
                                        fontSize: '0.9rem'
                                      }}>
                                        {capacity.capacity}
                                      </Typography>
                                    </TableCell>
                                    <TableCell>
                                      <Typography variant="body2" sx={{ 
                                        fontWeight: 500,
                                        color: 'text.primary',
                                        fontSize: '0.9rem'
                                      }}>
                                        {capacity.setup_time} min
                                      </Typography>
                                    </TableCell>
                                    <TableCell>
                                      <Typography variant="body2" sx={{ 
                                        fontWeight: 500,
                                        color: 'text.primary',
                                        fontSize: '0.9rem'
                                      }}>
                                        {capacity.cleanup_time} min
                                      </Typography>
                                    </TableCell>
                                  </TableRow>
                                ))}
                              </TableBody>
                            </Table>
                          </TableContainer>
                        ) : (
                          <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                            No product capacities defined
                          </Typography>
                        )}
                      </CardContent>
                    </Card>
                  </Grid>

                  {/* Notes Section */}
                  {selectedLocation.notes && (
                    <Grid item xs={12}>
                      <Card sx={{ 
                        borderRadius: 3,
                        border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
                        background: 'white',
                        transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                        cursor: 'default',
                        '&:hover': {
                          transform: 'translateY(-2px)',
                          boxShadow: `0 16px 48px ${alpha(theme.palette.primary.main, 0.15)}`,
                          border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`
                        }
                      }}>
                        <CardContent sx={{ p: 4 }}>
                          <Typography variant="h6" sx={{ fontWeight: 500, mb: 2, color: 'primary.main' }}>
                            Notes
                          </Typography>
                          <Typography variant="body1" sx={{ fontWeight: 500, lineHeight: 1.6, whiteSpace: 'pre-wrap' }}>
                            {selectedLocation.notes}
                          </Typography>
                        </CardContent>
                      </Card>
                    </Grid>
                  )}
                </Grid>
              </Box>
            </DialogContent>

            {/* Actions */}
            <Box sx={{ 
              p: 3, 
              pt: 1.5,
              background: 'white',
              borderTop: '1px solid',
              borderColor: alpha(theme.palette.grey[300], 0.5)
            }}>
              <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2 }}>
                {canEdit && (
                  <Button 
                  variant="outlined" 
                  onClick={() => {
                    handleCloseDetailDialog();
                    handleOpenDialog(selectedLocation);
                  }}
                  startIcon={<EditIcon />}
                  sx={{ 
                    borderRadius: 3, 
                    px: 3,
                    py: 1.5,
                    fontWeight: 500,
                    fontSize: '0.9rem',
                    border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
                    color: 'text.primary',
                    transition: 'all 0.2s cubic-bezier(0.4, 0.0, 0.2, 1)',
                    '&:hover': {
                      bgcolor: alpha(theme.palette.grey[100], 0.8),
      
                      transform: 'translateY(-1px)'
                    }
                  }}
                  >
                    Edit Location
                  </Button>
                )}
                <Button 
                  onClick={handleCloseDetailDialog} 
                  variant="contained"
                  sx={{ 
                    borderRadius: 3, 
                    px: 4,
                    py: 1.5,
                    fontWeight: 500,
                    fontSize: '0.9rem',
                    border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
                    boxShadow: `0 4px 12px ${alpha(theme.palette.primary.main, 0.2)}`,
                    background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
                    '&:hover': {
                      transform: 'translateY(-2px)',
      
                      boxShadow: `0 8px 20px ${alpha(theme.palette.primary.main, 0.3)}`
                    }
                  }}
                >
                  Close
                </Button>
              </Box>
            </Box>
          </>
        )}
      </Dialog>

      {/* Form Dialog */}
      <Dialog 
        open={dialogOpen} 
        onClose={handleCloseDialog} 
        maxWidth="lg" 
        fullWidth
        PaperProps={{
          sx: { 
            borderRadius: 4,
            border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
            boxShadow: '0 24px 48px rgba(0,0,0,0.15)',
            minHeight: '600px'
          }
        }}
      >
        <DialogTitle sx={{ 
          p: 3,
          borderBottom: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
          fontWeight: 500,
          fontSize: '1.25rem',
          background: 'white'
        }}>
          {editMode ? 'Edit Location' : 'Add New Location'}
        </DialogTitle>

        {/* Tabs */}
        <Box sx={{ borderBottom: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`, background: 'white' }}>
          <Tabs 
            value={activeTab} 
            onChange={(e, newValue) => setActiveTab(newValue)}
            sx={{
              '& .MuiTab-root': {
                fontWeight: 500,
                fontSize: '0.9rem',
                textTransform: 'none',
                minHeight: 48,
                px: 3
              }
            }}
          >
            <Tab 
              icon={<InfoIcon />} 
              label="General Information" 
              iconPosition="start"
              sx={{ gap: 1 }}
            />
            <Tab 
              icon={<InventoryIcon />} 
              label="Product Capacities" 
              iconPosition="start"
              sx={{ gap: 1 }}
            />
          </Tabs>
        </Box>

        <DialogContent sx={{ 
          p: 0,
          background: `linear-gradient(135deg, ${alpha(theme.palette.grey[100], 0.8)}, ${alpha(theme.palette.grey[50], 0.9)})`
        }}>
          {/* General Information Tab */}
          {activeTab === 0 && (
            <Box sx={{ p: 3 }}>
              <Typography variant="h6" sx={{ fontWeight: 500, mb: 3, color: 'primary.main' }}>
                General Information
              </Typography>
              <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 2.5 }}>
                <TextField
                  label="Location Name"
                  fullWidth
                  value={formData.name}
                  onChange={(e) => handleInputChange('name', e.target.value)}
                  required
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 3,
                      border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
                      background: 'white',
                      '& fieldset': { border: 'none' },
                      '&:hover': {
                        boxShadow: `0 4px 12px ${alpha(theme.palette.primary.main, 0.15)}`
                      },
                      '&.Mui-focused': {
                        boxShadow: `0 4px 16px ${alpha(theme.palette.primary.main, 0.25)}`
                      }
                    },
                    '& .MuiInputBase-input': {
                      fontSize: '0.9rem',
                      fontWeight: 500
                    },
                    '& .MuiInputLabel-root': {
                      fontSize: '0.9rem',
                      fontWeight: 500
                    }
                  }}
                />
                <FormControl fullWidth>
                  <InputLabel sx={{ fontSize: '0.9rem', fontWeight: 500 }}>Facility Type</InputLabel>
                  <Select
                    value={formData.facility_type}
                    label="Facility Type"
                    onChange={(e) => handleInputChange('facility_type', e.target.value)}
                    sx={{
                      borderRadius: 3,
                      border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
                      background: 'white',
                      '& .MuiSelect-select': {
                        fontSize: '0.9rem',
                        fontWeight: 500
                      },
                      '& .MuiOutlinedInput-notchedOutline': { border: 'none' }
                    }}
                  >
                    {facilityTypes.map((type) => (
                      <MenuItem key={type.value} value={type.value} sx={{ fontSize: '0.9rem', fontWeight: 500 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          {type.icon}
                          {type.label}
                        </Box>
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <TextField
                  label="Address"
                  fullWidth
                  value={formData.address}
                  onChange={(e) => handleInputChange('address', e.target.value)}
                  sx={{ 
                    gridColumn: { md: '1 / -1' },
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 3,
                      border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
                      background: 'white',
                      '& fieldset': { border: 'none' }
                    },
                    '& .MuiInputBase-input': {
                      fontSize: '0.9rem',
                      fontWeight: 500
                    },
                    '& .MuiInputLabel-root': {
                      fontSize: '0.9rem',
                      fontWeight: 500
                    }
                  }}
                />
                <TextField
                  label="City"
                  fullWidth
                  value={formData.city}
                  onChange={(e) => handleInputChange('city', e.target.value)}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 3,
                      border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
                      background: 'white',
                      '& fieldset': { border: 'none' }
                    },
                    '& .MuiInputBase-input': {
                      fontSize: '0.9rem',
                      fontWeight: 500
                    },
                    '& .MuiInputLabel-root': {
                      fontSize: '0.9rem',
                      fontWeight: 500
                    }
                  }}
                />
                <TextField
                  label="State"
                  fullWidth
                  value={formData.state}
                  onChange={(e) => handleInputChange('state', e.target.value)}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 3,
                      border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
                      background: 'white',
                      '& fieldset': { border: 'none' }
                    },
                    '& .MuiInputBase-input': {
                      fontSize: '0.9rem',
                      fontWeight: 500
                    },
                    '& .MuiInputLabel-root': {
                      fontSize: '0.9rem',
                      fontWeight: 500
                    }
                  }}
                />
                <TextField
                  label="ZIP Code"
                  fullWidth
                  value={formData.zip_code}
                  onChange={(e) => handleInputChange('zip_code', e.target.value)}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 3,
                      border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
                      background: 'white',
                      '& fieldset': { border: 'none' }
                    },
                    '& .MuiInputBase-input': {
                      fontSize: '0.9rem',
                      fontWeight: 500
                    },
                    '& .MuiInputLabel-root': {
                      fontSize: '0.9rem',
                      fontWeight: 500
                    }
                  }}
                />
                <TextField
                  label="Country"
                  fullWidth
                  value={formData.country}
                  onChange={(e) => handleInputChange('country', e.target.value)}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 3,
                      border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
                      background: 'white',
                      '& fieldset': { border: 'none' }
                    },
                    '& .MuiInputBase-input': {
                      fontSize: '0.9rem',
                      fontWeight: 500
                    },
                    '& .MuiInputLabel-root': {
                      fontSize: '0.9rem',
                      fontWeight: 500
                    }
                  }}
                />
                <TextField
                  label="Phone"
                  fullWidth
                  value={formData.phone}
                  onChange={(e) => handleInputChange('phone', e.target.value)}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 3,
                      border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
                      background: 'white',
                      '& fieldset': { border: 'none' }
                    },
                    '& .MuiInputBase-input': {
                      fontSize: '0.9rem',
                      fontWeight: 500
                    },
                    '& .MuiInputLabel-root': {
                      fontSize: '0.9rem',
                      fontWeight: 500
                    }
                  }}
                />
                <TextField
                  label="Email"
                  fullWidth
                  type="email"
                  value={formData.email}
                  onChange={(e) => handleInputChange('email', e.target.value)}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 3,
                      border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
                      background: 'white',
                      '& fieldset': { border: 'none' }
                    },
                    '& .MuiInputBase-input': {
                      fontSize: '0.9rem',
                      fontWeight: 500
                    },
                    '& .MuiInputLabel-root': {
                      fontSize: '0.9rem',
                      fontWeight: 500
                    }
                  }}
                />
                <TextField
                  label="Square Footage"
                  fullWidth
                  type="number"
                  value={formData.square_footage}
                  onChange={(e) => handleInputChange('square_footage', e.target.value)}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 3,
                      border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
                      background: 'white',
                      '& fieldset': { border: 'none' }
                    },
                    '& .MuiInputBase-input': {
                      fontSize: '0.9rem',
                      fontWeight: 500
                    },
                    '& .MuiInputLabel-root': {
                      fontSize: '0.9rem',
                      fontWeight: 500
                    }
                  }}
                />
                <FormControl fullWidth>
                  <InputLabel sx={{ fontSize: '0.9rem', fontWeight: 500 }}>Area Unit</InputLabel>
                  <Select
                    value={formData.square_footage_unit}
                    label="Area Unit"
                    onChange={(e) => handleInputChange('square_footage_unit', e.target.value)}
                    sx={{
                      borderRadius: 3,
                      border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
                      background: 'white',
                      '& .MuiSelect-select': {
                        fontSize: '0.9rem',
                        fontWeight: 500
                      },
                      '& .MuiOutlinedInput-notchedOutline': { border: 'none' }
                    }}
                  >
                    {areaUnits.map((unit) => (
                      <MenuItem key={unit.value} value={unit.value} sx={{ fontSize: '0.9rem', fontWeight: 500 }}>
                        {unit.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <Box sx={{ gridColumn: { md: '1 / -1' }, display: 'flex', gap: 4, alignItems: 'center' }}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={formData.is_active}
                        onChange={(e) => handleInputChange('is_active', e.target.checked)}
                      />
                    }
                    label={<Typography sx={{ fontSize: '0.9rem', fontWeight: 500 }}>Active Location</Typography>}
                  />
                  <FormControlLabel
                    control={
                      <Switch
                        checked={formData.is_main}
                        onChange={(e) => handleInputChange('is_main', e.target.checked)}
                      />
                    }
                    label={<Typography sx={{ fontSize: '0.9rem', fontWeight: 500 }}>Main Location</Typography>}
                  />
                </Box>
                <TextField
                  label="Notes"
                  fullWidth
                  multiline
                  rows={3}
                  value={formData.notes}
                  onChange={(e) => handleInputChange('notes', e.target.value)}
                  sx={{ 
                    gridColumn: { md: '1 / -1' },
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 3,
                      border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
                      background: 'white',
                      '& fieldset': { border: 'none' }
                    },
                    '& .MuiInputBase-input': {
                      fontSize: '0.9rem',
                      fontWeight: 500
                    },
                    '& .MuiInputLabel-root': {
                      fontSize: '0.9rem',
                      fontWeight: 500
                    }
                  }}
                />
              </Box>
            </Box>
          )}

          {/* Product Capacities Tab */}
          {activeTab === 1 && (
            <ProductCapacitiesTab 
              locationId={formData.id} 
              isNewLocation={!editMode}
              formCapacities={formCapacities}
              setFormCapacities={setFormCapacities}
            />
          )}
        </DialogContent>
        <DialogActions sx={{ 
          p: 3, 
          borderTop: `1px solid ${alpha(theme.palette.grey[300], 0.8)}`,
          background: 'white',
          gap: 2
        }}>
          <Button 
            onClick={handleCloseDialog} 
            disabled={submitting}
            sx={{
              borderRadius: 3,
              px: 3,
              py: 1.5,
              fontWeight: 500,
              fontSize: '0.9rem',
              border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
              transition: 'all 0.2s cubic-bezier(0.4, 0.0, 0.2, 1)',
              '&:hover': {

                transform: 'translateY(-1px)',
                boxShadow: `0 4px 12px ${alpha(theme.palette.primary.main, 0.2)}`
              }
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            variant="contained"
            disabled={submitting || !formData.name.trim()}
            sx={{ 
              borderRadius: 3,
              px: 4,
              py: 1.5,
              fontWeight: 500,
              fontSize: '0.9rem',
              border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
              boxShadow: `0 6px 18px ${alpha(theme.palette.primary.main, 0.25)}`,
              transition: 'all 0.2s cubic-bezier(0.4, 0.0, 0.2, 1)',
              '&:hover': {
                transform: 'translateY(-1px) scale(1.02)',
                boxShadow: `0 8px 24px ${alpha(theme.palette.primary.main, 0.35)}`
              }
            }}
          >
            {submitting ? 'Saving...' : editMode ? 'Update Location' : 'Add Location'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteConfirmOpen}
        onClose={handleCancelDelete}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: { 
            borderRadius: 4,
            border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
            boxShadow: '0 24px 48px rgba(0,0,0,0.15)'
          }
        }}
      >
        <DialogTitle sx={{ 
          pb: 2,
          fontWeight: 500,
          fontSize: '1.25rem'
        }}>
          Confirm Delete
        </DialogTitle>
        <DialogContent>
          <Typography sx={{ fontWeight: 500 }}>
            Are you sure you want to delete "{locationToDelete?.name}"? This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ p: 2.5, gap: 1 }}>
          <Button
            onClick={handleCancelDelete}
            variant="outlined"
            sx={{ 
              borderRadius: 3,
              px: 3,
              py: 1.5,
              fontWeight: 500,
              fontSize: '0.9rem',
              border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
              transition: 'all 0.2s cubic-bezier(0.4, 0.0, 0.2, 1)',
              '&:hover': {

                transform: 'translateY(-1px)',
                boxShadow: `0 4px 12px ${alpha(theme.palette.primary.main, 0.2)}`
              }
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleConfirmDelete}
            variant="contained"
            color="error"
            sx={{ 
              borderRadius: 3,
              px: 3,
              py: 1.5,
              fontWeight: 500,
              fontSize: '0.9rem',
              border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
              boxShadow: `0 6px 18px ${alpha(theme.palette.error.main, 0.25)}`,
              transition: 'all 0.2s cubic-bezier(0.4, 0.0, 0.2, 1)',
              '&:hover': {
                transform: 'translateY(-1px) scale(1.02)',
                boxShadow: `0 8px 24px ${alpha(theme.palette.error.main, 0.35)}`
              }
            }}
          >
            Delete Location
          </Button>
        </DialogActions>
      </Dialog>

      {/* Clean Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert 
          onClose={handleCloseSnackbar} 
          severity={snackbar.severity}
          sx={{ 
            width: '100%',
            borderRadius: 3,
            border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
            boxShadow: `0 8px 24px ${alpha(theme.palette.grey[900], 0.15)}`,
            '& .MuiAlert-message': {
              fontWeight: 500,
              fontSize: '0.9rem'
            }
          }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
        </Container>
      </Box>
    </Fade>
  );
};

export default LocationsPage;